import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import Translator, { __ } from "services/Translator";
import HubToast, { showHubToast } from "components/goldenCloverHub/HubToast";
import { getVipMedalSrc } from "components/goldenCloverHub/vipTierAssets";
import { getRewardsMockState } from "./rewardsMockData";

// Empty (non-fabricated) fallback used when a real player has a live but
// partial payload — we must NEVER substitute mock numbers as if they were real.
const EMPTY_STATE = {
  vip: {
    tierKey: "",
    tierName: "",
    progressPct: 0,
    ptsLabel: "",
    nextTierName: "",
    toNextLabel: "",
    benefits: [],
  },
  balances: {
    cloverCoins: { amount: "—", weekDelta: "" },
    freeSpins: { amount: "—", meta: "" },
    bonuses: [],
  },
  streak: { days: 0, dayPills: [], todayReward: "" },
  badges: [],
  activity: [],
  earnedCount: 0,
  totalBadges: 0,
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function")
      return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () =>
      mq.removeEventListener
        ? mq.removeEventListener("change", on)
        : mq.removeListener(on);
  }, []);
  return reduced;
}

// Inline branded icon sprite (gold/green, currentColor). Self-contained — no
// external asset files — so the build can never break on a missing image.
// Rendered medal art for badges still arrives via badge.icon_url (BadgeIcon).
function GcIconDefs() {
  return (
    <svg
      width="0"
      height="0"
      aria-hidden="true"
      focusable="false"
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
    >
      <defs>
        <symbol id="gc-clover" viewBox="0 0 24 24">
          <g transform="translate(12 11)" fill="currentColor">
            <ellipse cx="0" cy="-5" rx="4" ry="5" />
            <ellipse cx="5" cy="0" rx="5" ry="4" />
            <ellipse cx="0" cy="5" rx="4" ry="5" />
            <ellipse cx="-5" cy="0" rx="5" ry="4" />
            <rect x="-0.6" y="3" width="1.2" height="7" rx="0.6" />
          </g>
        </symbol>
        <symbol id="gc-spin" viewBox="0 0 24 24">
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            d="M19 12a7 7 0 1 1-2.1-5"
          />
          <path fill="currentColor" d="M17 3.5l.7 4.2-4.2-.7z" />
        </symbol>
        <symbol id="gc-gift" viewBox="0 0 24 24">
          <g fill="currentColor">
            <rect x="4" y="10" width="16" height="10" rx="1.5" />
            <rect x="3" y="7" width="18" height="4" rx="1" />
            <rect x="11" y="7" width="2" height="13" />
            <path d="M12 7c-1-3-5-3-5-1 0 1.6 3 1 5 1zm0 0c1-3 5-3 5-1 0 1.6-3 1-5 1z" />
          </g>
        </symbol>
        <symbol id="gc-flame" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M13 2c1 3-1 5-2.5 6.5C9 10 8 11.5 8 13.5A6 6 0 0 0 20 14c0-3-2-5-3.5-7 .5 2-.5 3-1.5 3.5C15.5 8 15 4 13 2z"
          />
        </symbol>
        <symbol id="gc-lock" viewBox="0 0 24 24">
          <rect x="5" y="10" width="14" height="10" rx="2" fill="currentColor" />
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            d="M8 10V7a4 4 0 0 1 8 0v3"
          />
        </symbol>
        <symbol id="gc-trophy" viewBox="0 0 24 24">
          <g fill="currentColor">
            <path d="M7 4h10v3a5 5 0 0 1-10 0V4z" />
            <rect x="10" y="12" width="4" height="4" />
            <rect x="7" y="18" width="10" height="2.5" rx="1" />
          </g>
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            d="M7 5H4v2a3 3 0 0 0 3 3m10-5h3v2a3 3 0 0 1-3 3"
          />
        </symbol>
        <symbol id="gc-check" viewBox="0 0 24 24">
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 12.5l4 4 10-10"
          />
        </symbol>
      </defs>
    </svg>
  );
}

function GcIcon({ name, className }) {
  return (
    <svg
      className={`gc-ic${className ? ` ${className}` : ""}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <use href={`#gc-${name}`} />
    </svg>
  );
}

function BadgeIcon({ badge, className, as: Tag = "span" }) {
  if (badge.icon_url) {
    return (
      <Tag className={className}>
        <img src={badge.icon_url} alt="" className="badge-icon-img" />
      </Tag>
    );
  }
  // Operator-entered emoji fallback until rendered medal art ships.
  return (
    <Tag className={className} aria-hidden="true">
      {badge.icon}
    </Tag>
  );
}

// Shared accessible progress meter — every bar in the engine now exposes
// role=progressbar semantics instead of being a bare div.
function Meter({ value, max = 100, label, className = "bar", barRef, barStyle }) {
  const num = Number(value) || 0;
  const pct = max > 0 ? Math.max(0, Math.min(100, (num / max) * 100)) : 0;
  return (
    <div
      className={className}
      role="progressbar"
      aria-valuenow={Math.round(num)}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
    >
      <b ref={barRef} style={barStyle ?? { width: `${pct}%` }} />
    </div>
  );
}

function AchievementTooltip({ badge, anchorRect, visible, id }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!visible || !badge || !anchorRect || !ref.current) {
      setPos(null);
      return;
    }
    const h = ref.current.offsetHeight || 120;
    const w = ref.current.offsetWidth || 288;
    let below = false;
    let top = anchorRect.top - 12 - h;
    if (top < 10) {
      top = anchorRect.bottom + 12;
      below = true;
    }
    let left = anchorRect.left + anchorRect.width / 2 - w / 2;
    left = Math.max(8, Math.min(window.innerWidth - w - 8, left));
    setPos({ left, top, below });
  }, [visible, badge, anchorRect]);

  if (!visible || !badge || !anchorRect) return null;

  const earned = !badge.locked;
  const tierMap = {
    bronze: __("Bronze"),
    silver: __("Silver"),
    gold: __("Gold"),
    diamond: __("Diamond"),
    locked: __("Locked"),
  };
  const tierLabel =
    !badge.tier || badge.tier === "locked"
      ? tierMap.locked
      : tierMap[badge.tier] || badge.tier;

  return (
    <div
      id={id}
      ref={ref}
      className={`ach-tooltip show ${pos && pos.below ? "below" : ""}`}
      style={{
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        position: "fixed",
        visibility: pos ? "visible" : "hidden",
      }}
      role="tooltip"
    >
      <div className="ach-tooltip-card">
        <div className="title">
          <BadgeIcon badge={badge} className="ic" />
          <span className="t-name">{badge.name}</span>
        </div>
        <div className="desc">
          {!earned ? `${__("How to earn")}: ` : ""}
          {badge.condition}
        </div>
        <div className="footer">
          <span className={`status ${earned ? "" : "locked"}`}>
            <GcIcon name={earned ? "check" : "lock"} className="status-ic" />{" "}
            {earned ? __("Earned") : __("Locked")}
          </span>
          <span className="tier">· {tierLabel}</span>
        </div>
      </div>
    </div>
  );
}

export default function RewardsContent({ data: dataProp, isPreviewDemo }) {
  const preview = isPreviewDemo ?? dataProp?.isPreviewDemo ?? false;
  const mock = useMemo(() => getRewardsMockState(), []);
  const reducedMotion = usePrefersReducedMotion();

  // Mock is a demo aid ONLY in preview mode. In real mode we fall back to an
  // honest empty state — never to fabricated numbers.
  const fallback = preview ? mock : EMPTY_STATE;
  const data = dataProp || fallback;

  const vip = data.vip || fallback.vip;
  const balances = data.balances || fallback.balances;
  const streak = data.streak || fallback.streak;
  const badges = data.badges || fallback.badges;
  const activity = data.activity || fallback.activity;
  const earnedCount = data.earnedCount ?? fallback.earnedCount;
  const totalBadges = data.totalBadges ?? badges.length;

  const [wallExpanded, setWallExpanded] = useState(false);
  const [wallSlim, setWallSlim] = useState(true);
  const [extraReveal, setExtraReveal] = useState(false);
  const [tooltipBadge, setTooltipBadge] = useState(null);
  const [tooltipKey, setTooltipKey] = useState(null);
  const [tooltipRect, setTooltipRect] = useState(null);
  const vipBarRef = useRef(null);
  const collapseTimerRef = useRef(null);

  useEffect(() => {
    const bar = vipBarRef.current;
    if (!bar) return;
    const target = `${vip.progressPct}%`;
    if (reducedMotion) {
      bar.style.width = target;
      return;
    }
    bar.style.width = "0%";
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bar.style.width = target;
      });
    });
    return () => cancelAnimationFrame(id);
  }, [vip.progressPct, reducedMotion]);

  const toggleWall = useCallback(
    (e) => {
      e.preventDefault();
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
      if (wallExpanded) {
        setExtraReveal(false);
        collapseTimerRef.current = setTimeout(() => {
          setWallExpanded(false);
          setWallSlim(true);
        }, 320);
      } else {
        setWallSlim(false);
        setWallExpanded(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setExtraReveal(true));
        });
      }
    },
    [wallExpanded]
  );

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    };
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltipBadge(null);
    setTooltipKey(null);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", hideTooltip, { passive: true });
    return () => window.removeEventListener("scroll", hideTooltip);
  }, [hideTooltip]);

  const showTooltipFor = (badge, key, el) => {
    if (!badge?.condition) return;
    setTooltipBadge(badge);
    setTooltipKey(key);
    setTooltipRect(el.getBoundingClientRect());
  };

  const toggleTooltip = (badge, key, el) => {
    if (tooltipKey === key) hideTooltip();
    else showTooltipFor(badge, key, el);
  };

  const medalSrc = getVipMedalSrc(vip.tierKey);
  const hasVip = !!vip.tierName;
  const vipProgressLabel = `${__("VIP progress")}: ${Math.round(
    Number(vip.progressPct) || 0
  )}%${
    vip.nextTierName ? ` — ${vip.toNextLabel} ${__("to")} ${vip.nextTierName}` : ""
  }`;

  let extraIdx = -1;

  return (
    <div className="rewards-page container-xxl">
      <GcIconDefs />
      <HubToast />
      <>
        {preview && (
          <p className="rewards-page__preview-note" role="status">
            {__(
              "Preview mode — balances and achievements will sync when the rewards API is live."
            )}
          </p>
        )}

        <div className="page-head">
          <h1>{__("Your rewards")}</h1>
          <span className="sub">
            {preview
              ? __("Sample data")
              : data.lastUpdateSec != null
              ? `${__("Last update")} · ${data.lastUpdateSec} ${__("sec ago")}`
              : __("Updated just now")}
          </span>
        </div>

        {hasVip ? (
          <section className="vip-widget">
            <div className="vip-medal" title={vip.tierName}>
              <img src={medalSrc} alt={vip.tierName} />
            </div>
            <div className="vip-meta">
              <div className="row1">
                <span className="tier-name">{vip.tierName}</span>
                <span className="pts">{vip.ptsLabel}</span>
              </div>
              <Meter
                value={vip.progressPct}
                max={100}
                label={vipProgressLabel}
                className="bar"
                barRef={vipBarRef}
                barStyle={{ width: `${vip.progressPct}%` }}
              />
              {vip.nextTierName ? (
                <div className="to-next">
                  <b>{vip.toNextLabel}</b> {__("to")} {vip.nextTierName}
                </div>
              ) : (
                <div className="to-next">
                  <b>{__("Top tier reached")}</b>
                </div>
              )}
              {(vip.benefits || []).length > 0 && (
                <ul className="vip-benefits">
                  {(vip.benefits || []).slice(0, 4).map((benefit, i) => (
                    <li key={i} className={benefit.highlight ? "hl" : ""}>
                      <span className="b-label">{benefit.label}</span>
                      <span className="b-value">{benefit.value}</span>
                    </li>
                  ))}
                </ul>
              )}
              {vip.nextTierName &&
                Array.isArray(vip.nextBenefits) &&
                vip.nextBenefits.length > 0 && (
                  <ul
                    className="vip-next-unlocks"
                    aria-label={`${vip.nextTierName} ${__("unlocks")}`}
                  >
                    {vip.nextBenefits.slice(0, 3).map((benefit, i) => (
                      <li key={i}>
                        <GcIcon name="lock" className="ic" />
                        <span>
                          {typeof benefit === "string"
                            ? benefit
                            : benefit.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
            <Link
              hreflang={Translator.getSelectedLanguage().code}
              to="/vip"
              className="vip-link"
            >
              {__("View VIP programme →")}
            </Link>
          </section>
        ) : (
          !preview && (
            <section className="vip-widget vip-widget--empty">
              <p className="rewards-empty">
                {__("Your VIP progress will appear here once you start playing.")}
              </p>
            </section>
          )
        )}

        <div className="balance-row">
          <div className="balance-card">
            <div className="head">
              <span className="ic">
                <GcIcon name="clover" />
              </span>
              <span className="label">{__("Clover Coins")}</span>
            </div>
            <div className="big">
              {balances.cloverCoins?.amount}
              <span className="unit">CC</span>
            </div>
            <div className="small">
              <b>{balances.cloverCoins?.weekDelta}</b> {__("this week")}
            </div>
          </div>
          <div className="balance-card">
            <div className="head">
              <span className="ic">
                <GcIcon name="spin" />
              </span>
              <span className="label">{__("Free Spins")}</span>
            </div>
            <div className="big">
              {balances.freeSpins?.amount}
              <span className="unit">{__("spins")}</span>
            </div>
            <div className="small">{balances.freeSpins?.meta}</div>
          </div>
          <div className="balance-card bonus">
            <div className="head">
              <span className="ic">
                <GcIcon name="gift" />
              </span>
              <span className="label">{__("Active Bonuses")}</span>
            </div>
            {(balances.bonuses || []).length > 0 ? (
              (balances.bonuses || []).map((bonus) => (
                <React.Fragment key={bonus.name}>
                  <div className="bonus-line">
                    <span className="nm">{bonus.name}</span>
                    <span className="amt">{bonus.amount}</span>
                  </div>
                  <Meter
                    value={bonus.wagerPct}
                    max={100}
                    label={`${bonus.name}: ${__("wagering")} ${bonus.wagerPct}%`}
                    className="wbar"
                  />
                </React.Fragment>
              ))
            ) : (
              <div className="bonus-empty">{__("No active bonuses")}</div>
            )}
          </div>
        </div>

        <div className="streak-strip">
          <div className="flame-wrap">
            <span className="flame">
              <GcIcon name="flame" />
            </span>
            <div className="flame-text">
              <div className="n">
                {streak.days} {__("days")}
              </div>
              <div className="l">{__("Current streak")}</div>
            </div>
          </div>
          <div
            className="streak-days-mini"
            role="list"
            aria-label={__("Streak days")}
          >
            {(streak.dayPills || []).map((pill, i) => {
              const isMega = pill.status === "mega";
              return (
                <div
                  key={`${pill.label}-${i}`}
                  className={`d ${pill.status}`}
                  role="listitem"
                  aria-label={isMega ? __("Milestone reward") : undefined}
                >
                  {isMega ? <GcIcon name="trophy" /> : pill.label}
                </div>
              );
            })}
          </div>
          <div className="next-rew">
            {__("Today's reward")}
            <b>{streak.todayReward}</b>
          </div>
        </div>

        <div className="wall-section">
          <div className="section-title">
            <h2>{__("Achievement wall")}</h2>
            <small>
              {wallExpanded
                ? `${earnedCount} ${__("earned")} · ${
                    badges.length - earnedCount
                  } ${__("still to unlock")}`
                : `${earnedCount} ${__("of")} ${badges.length} ${__("earned")}`}
            </small>
            {badges.length > 0 && (
              <button type="button" className="more" onClick={toggleWall}>
                {wallExpanded
                  ? __("Show fewer ›")
                  : `${__("View all")} ${totalBadges} ›`}
              </button>
            )}
          </div>
          <section className="panel wall">
            {badges.length > 0 ? (
              <div className={`wall-grid ${wallSlim ? "wall-grid-slim" : ""}`}>
                {badges.map((badge, i) => {
                  const isExtra = badge.extra;
                  if (isExtra) extraIdx += 1;
                  const revealIndex = isExtra ? extraIdx : 0;
                  const tierClass = badge.locked
                    ? "locked"
                    : badge.tier || "bronze";
                  const extraClass = isExtra && extraReveal ? "show" : "";
                  const active = tooltipKey === i;
                  const statusText = badge.locked ? __("locked") : __("earned");
                  const ariaLabel = `${badge.name}, ${statusText}${
                    badge.condition ? ` — ${badge.condition}` : ""
                  }`;

                  const base = { "--i": revealIndex };
                  let style = base;
                  if (isExtra && !wallExpanded) {
                    style = { ...base, display: "none" };
                  } else if (isExtra && wallExpanded && !extraReveal) {
                    style = { ...base, display: "flex", opacity: 0 };
                  }

                  return (
                    <div
                      key={`${badge.id ?? badge.name}-${i}`}
                      role="button"
                      tabIndex={0}
                      aria-label={ariaLabel}
                      aria-describedby={active ? "ach-tooltip" : undefined}
                      data-name={badge.name}
                      data-cond={badge.condition}
                      className={`badge ${tierClass} ${isExtra ? "extra" : ""} ${extraClass}`}
                      style={style}
                      onMouseEnter={(e) =>
                        showTooltipFor(badge, i, e.currentTarget)
                      }
                      onMouseLeave={hideTooltip}
                      onFocus={(e) => showTooltipFor(badge, i, e.currentTarget)}
                      onBlur={hideTooltip}
                      onClick={(e) => toggleTooltip(badge, i, e.currentTarget)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleTooltip(badge, i, e.currentTarget);
                        } else if (e.key === "Escape") {
                          hideTooltip();
                        }
                      }}
                    >
                      <BadgeIcon badge={badge} className="medal" as="div" />
                      <div className="nm">{badge.name}</div>
                      <div className="dt">{badge.date}</div>
                      {badge.locked && (
                        <span className="lock-ic" aria-hidden="true">
                          <GcIcon name="lock" />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="rewards-empty">
                {__(
                  "No achievements yet — start playing to earn your first medal."
                )}
              </p>
            )}
          </section>
        </div>

        <AchievementTooltip
          id="ach-tooltip"
          badge={tooltipBadge}
          anchorRect={tooltipRect}
          visible={!!tooltipBadge}
        />

        <div className="section-head">
          <h2>{__("Recent")}</h2>
          <span className="sub">{__("Last 7 days")}</span>
        </div>
        <div className="activity">
          {activity.length > 0 ? (
            activity.map((row, i) => (
              <div className="activity-row" key={`${row.title}-${i}`}>
                <div className="ic" aria-hidden="true">
                  {row.icon}
                </div>
                <div className="info">
                  <div className="w">{row.title}</div>
                  <div className="t">{row.time}</div>
                </div>
                <div className="amt">{row.amount}</div>
              </div>
            ))
          ) : (
            <p className="rewards-empty">
              {__("No recent reward activity yet.")}
            </p>
          )}
          <div className="activity-foot">
            <Link
              hreflang={Translator.getSelectedLanguage().code}
              to="/history"
              onClick={(e) => {
                if (preview) {
                  e.preventDefault();
                  showHubToast(
                    __("Full history"),
                    __("Opens transaction history")
                  );
                }
              }}
            >
              {__("View full history →")}
            </Link>
          </div>
        </div>
      </>
    </div>
  );
}
